/* premium-hero-motion.js - ported from the approved Codex prototype (hero-motion.js).
 * Three soft, bounded floating cans: padded elliptical boundaries, no physics library, no
 * per-frame layout reads. Adds the section's pause toggle + pointer parallax. Theme-safe:
 * scoped to each .premium-hero, cleaned up on shopify:section:unload, shares the body
 * .site-motion-paused pause state with the pouch-story motion. Respects reduced motion,
 * hidden tabs, and off-screen (suspends). Physics constants preserved from the prototype.
 */
(function () {
  var reduced = matchMedia('(prefers-reduced-motion: reduce)');

  function initHeroMotion(hero) {
    if (!hero || hero.__premiumMotion) return;
    var stage = hero.querySelector('.stage');
    if (!stage) return;
    var flatten = 0.84, gutter = 10;
    // CALM mode (Danny phone QC 2026-09-20): on the tucked phone hero the three cans sit in a tight, larger
    // triangle. The free drift below sends each can down its own path (up to ~30px sideways, ~38px
    // vertically, different rhythms) and the collision pass shoves them apart whenever they touch, so the
    // composition never held still. Calm mode makes them read as ONE group, the way a single pre-rendered
    // loop does: a small, slow, nearly shared bob around each can's CSS rest position, a slight lag per can
    // so it still feels alive, and no collision pass (they can no longer reach each other). Desktop and the
    // untucked hero keep the original drift.
    var narrow = matchMedia('(max-width: 749px)');
    function calm() { return narrow.matches && hero.classList.contains('premium-hero--tuck'); }
    var bodies = [].slice.call(stage.querySelectorAll('.can')).map(function (el, i) {
      return { el: el, inner: el.querySelector('.can-inner'), i: i, x: 0, y: 0, vx: 0, vy: 0, r: 0, w: 0, ax: 0, ay: 0 };
    });
    if (bodies.length < 1) return;
    var width = 0, height = 0, frame = 0, last = 0, time = 0, visible = true, dirty = true, initialized = false;
    stage.classList.add('collision-stage');

    function stopped() { return reduced.matches || document.hidden || !visible || document.body.classList.contains('site-motion-paused'); }
    function clampBody() {
      // CLS fix: cans are CSS-anchored and the drift amplitude is small, so no hard clamp — this
      // also respects the intentional partly-off-stage CSS placement (e.g. neon at right:-6%).
    }
    function separate(iterations) {
      if (iterations === undefined) iterations = 16;
      for (var pass = 0; pass < iterations; pass++) {
        for (var i = 0; i < bodies.length; i++) for (var j = i + 1; j < bodies.length; j++) {
          var a = bodies[i], b = bodies[j], dx = b.x - a.x, dy = b.y - a.y;
          var distance = Math.hypot(dx, dy) || 0.001, minimum = a.r + b.r;
          if (distance >= minimum) continue;
          var nx = dx / distance, ny = dy / distance, overlap = minimum - distance + 0.02;
          a.x -= nx * overlap / 2; a.y -= ny * overlap / 2;
          b.x += nx * overlap / 2; b.y += ny * overlap / 2;
          var approach = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
          if (approach < 0) { var impulse = -(1 + 0.82) * approach / 2; a.vx -= impulse * nx; a.vy -= impulse * ny; b.vx += impulse * nx; b.vy += impulse * ny; }
        }
        bodies.forEach(function (b) { clampBody(b); });
      }
    }
    function measure() {
      width = stage.clientWidth; height = stage.clientHeight;
      bodies.forEach(function (b) {
        b.w = b.el.offsetWidth;
        var h = b.el.offsetHeight || b.w;
        b.r = b.w * 0.5 + gutter / 2;
        // CLS fix (perf-2026-09-08): anchor each body at its CSS-rendered CENTER. The cans keep their
        // CSS left/top; drift is a composited transform DELTA around this anchor, so the first paint
        // delta is 0 and the motion handoff moves nothing in layout (no CLS). Resting look = CSS layout.
        b.ax = b.el.offsetLeft + b.w / 2;
        b.ay = b.el.offsetTop + h / 2;
        b.x = b.ax; b.y = b.ay; b.vx = 0; b.vy = 0;
      });
      paint(); dirty = false; initialized = true;
    }
    function paint() {
      bodies.forEach(function (b) {
        b.inner.style.transform = 'translate3d(' + (b.x - b.ax).toFixed(2) + 'px,' + (b.y - b.ay).toFixed(2) + 'px,0)';
      });
    }
    function tick(now) {
      frame = 0;
      if (dirty) measure();
      if (stopped()) { last = 0; return; }
      var dt = last ? Math.min((now - last) / 1000, 0.035) : 1 / 60; last = now; time += dt;
      var isCalm = calm();
      bodies.forEach(function (b) {
        var amplitude = isCalm ? Math.min(5, width * 0.014) : Math.min(22, width * 0.045);
        var ampY = isCalm ? 7 : 16 / flatten;
        // calm: near-shared phase (small per-can lag) and a slower rhythm; free: independent paths.
        var fx = isCalm ? 0.34 : 0.47, fy = isCalm ? 0.42 : 0.58, ox = b.i * (isCalm ? 0.45 : 2.1), oy = b.i * (isCalm ? 0.55 : 2.5);
        // Drift EASES FROM 0: subtract each can's phase offset at t=0 so the first frame target is
        // exactly the rest anchor (no lurch, first frame a true no-op — bbox identical to pre-init).
        var tx = b.ax + (Math.sin(time * fx + ox) - Math.sin(ox)) * amplitude;
        var ty = b.ay + (Math.sin(time * fy + oy) - Math.sin(oy)) * ampY;
        b.vx += (tx - b.x) * 0.38 * dt; b.vy += (ty - b.y) * 0.38 * dt;
        var damping = Math.exp(-0.32 * dt); b.vx *= damping; b.vy *= damping;
        var speed = Math.hypot(b.vx, b.vy); if (speed > 19) { b.vx *= 19 / speed; b.vy *= 19 / speed; }
        b.x += b.vx * dt; b.y += b.vy * dt;
      });
      if (!isCalm) separate();
      paint(); frame = requestAnimationFrame(tick);
    }
    function wake() {
      if (stopped() && !dirty) { if (frame) cancelAnimationFrame(frame); frame = 0; last = 0; return; }
      if (!frame) frame = requestAnimationFrame(tick);
    }

    var ro = new ResizeObserver(function () { dirty = true; wake(); }); ro.observe(stage);
    var io = new IntersectionObserver(function (entries) { visible = entries[0].isIntersecting; hero.classList.toggle('collision-hidden', !visible); wake(); }, { threshold: 0.04 }); io.observe(hero);
    var mo = new MutationObserver(wake); mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    reduced.addEventListener('change', wake);
    document.addEventListener('visibilitychange', wake);

    // Pause toggle (shares body.site-motion-paused so the pouch story pauses in sync)
    var toggle = hero.querySelector('.motion-toggle');
    function setMotion() {
      var paused = document.body.classList.toggle('site-motion-paused');
      hero.classList.toggle('motion-paused', paused);
      document.querySelectorAll('.motion-toggle').forEach(function (btn) {
        btn.setAttribute('aria-pressed', String(paused));
        btn.textContent = paused ? 'Resume motion' : 'Pause motion';
      });
    }
    if (toggle) toggle.addEventListener('click', setMotion);

    // Pointer parallax (mouse only; suppressed under reduced motion / paused)
    function onMove(e) {
      if (reduced.matches || hero.classList.contains('motion-paused') || e.pointerType !== 'mouse') return;
      var r = hero.getBoundingClientRect();
      stage.style.setProperty('--px', ((e.clientX - r.left - r.width / 2) * 0.012) + 'px');
      stage.style.setProperty('--py', ((e.clientY - r.top - r.height / 2) * 0.014) + 'px');
    }
    function onLeave() { stage.style.setProperty('--px', '0px'); stage.style.setProperty('--py', '0px'); }
    hero.addEventListener('pointermove', onMove);
    hero.addEventListener('pointerleave', onLeave);

    hero.__premiumMotion = {
      destroy: function () {
        if (frame) cancelAnimationFrame(frame);
        ro.disconnect(); io.disconnect(); mo.disconnect();
        reduced.removeEventListener('change', wake);
        document.removeEventListener('visibilitychange', wake);
        hero.removeEventListener('pointermove', onMove);
        hero.removeEventListener('pointerleave', onLeave);
        delete hero.__premiumMotion;
      }
    };
    measure(); wake();
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var heroes = scope.querySelectorAll('.premium-hero');
    for (var i = 0; i < heroes.length; i++) initHeroMotion(heroes[i]);
    if (scope.classList && scope.classList.contains('premium-hero')) initHeroMotion(scope);
  }
  // PERF (perf-2026-09-08): start the drift/parallax only AFTER window load, in idle time, so it
  // never competes with the LCP paint or adds long tasks during load. The CSS ph-float keeps the
  // cans gently alive until then; the handoff is a no-op transform (CSS-anchored), so no CLS.
  function deferMotion() {
    if ('requestIdleCallback' in window) requestIdleCallback(function () { initAll(document); }, { timeout: 3000 });
    else setTimeout(function () { initAll(document); }, 300);
  }
  if (window.Shopify && Shopify.designMode) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); });
    else initAll(document);
  } else if (document.readyState === 'complete') {
    deferMotion();
  } else {
    window.addEventListener('load', deferMotion, { once: true });
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var heroes = e.target.querySelectorAll ? e.target.querySelectorAll('.premium-hero') : [];
    for (var i = 0; i < heroes.length; i++) if (heroes[i].__premiumMotion) heroes[i].__premiumMotion.destroy();
  });
})();
